import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CalendarIcon, Search } from 'lucide-react';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '@/lib/utils/date';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { StatusFilterPills } from '@/components/common/StatusFilterPills';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import type { Payment, PaymentFilters, PaymentStatus } from '../types';

interface PaymentsDataTableProps {
  data: Payment[];
  columns: ColumnDef<Payment>[];
  isLoading: boolean;
  filters: PaymentFilters;
  updateFilter: <K extends keyof PaymentFilters>(key: K, value: PaymentFilters[K]) => void;
  updateFilters?: (updates: Partial<PaymentFilters>) => void;
  onClearFilters?: () => void;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

export function PaymentsDataTable({
  data,
  columns,
  isLoading,
  filters,
  updateFilter,
  updateFilters,
  onClearFilters,
  controlsContainer,
  tableZoom: tableZoomProp,
}: PaymentsDataTableProps) {
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('payments');
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  // Debounced search
  const [searchValue, setSearchValue] = useState(filters.s ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const trimmed = searchValue.trim();
      const newValue = trimmed.length >= 2 ? trimmed : undefined;
      if (newValue !== filters.s) {
        updateFilter('s', newValue);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchValue]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  const {
    'createdAt.min': minDate,
    'createdAt.max': maxDate,
  } = filters;

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters['createdAt.min'] || filters['createdAt.max']) count++;
    if (filters.methodId) count++;
    if (filters.billId) count++;
    if (filters.clientId) count++;
    if (filters.unallocated) count++;
    if (filters.s) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    setSearchValue('');
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* Toolbar: status pills + table controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-2">
        <StatusFilterPills
          value={filters.status}
          onChange={(v) => updateFilter('status', v as PaymentStatus | undefined)}
          // Payments are recorded as ACCEPTED (2026-07-17); NEW and PENDING are
          // retired and get no pill. DENIED keeps one so legacy rows stay
          // findable — the badge still renders every status.
          options={[
            { value: 'ACCEPTED', label: t('status.ACCEPTED'), activeClassName: 'data-[state=on]:bg-emerald-500' },
            { value: 'DENIED', label: t('status.DENIED'), activeClassName: 'data-[state=on]:bg-red-500' },
          ]}
        />
        <div className="flex items-center gap-2">
          {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
        </div>
      </div>

      {/* Filters panel */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        gridCols={6}
      >
        {/* Created Date Range */}
        <FilterField label={t('createdDateRange')}>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
              >
                <span className="truncate">
                  {minDate && maxDate
                    ? `${format(new Date(minDate), 'MM/dd/yy', { locale: dateFnsLocale })} - ${format(new Date(maxDate), 'MM/dd/yy', { locale: dateFnsLocale })}`
                    : t('all')}
                </span>
                <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar
                mode="range"
                selected={{
                  from: minDate ? new Date(minDate) : undefined,
                  to: maxDate ? new Date(maxDate) : undefined,
                }}
                onSelect={(range) => {
                  if (updateFilters) {
                    updateFilters({
                      'createdAt.min': range?.from?.toISOString(),
                      'createdAt.max': range?.to?.toISOString(),
                    });
                  } else {
                    updateFilter('createdAt.min', range?.from?.toISOString());
                    updateFilter('createdAt.max', range?.to?.toISOString());
                  }
                }}
                numberOfMonths={2}
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </FilterField>

        {/* Payment Method */}
        <FilterField label={t('paymentMethod')}>
          <UniversalCombobox
            url="/payment-methods"
            labelKey="name"
            valueKey="id"
            selectedId={filters.methodId}
            onSelectItem={(item) =>
              updateFilter('methodId', item ? (item['id'] as number) : undefined)
            }
            placeholder={t('all')}
            className="w-full"
          />
        </FilterField>

        {/* Bill */}
        <FilterField label={t('bill')}>
          <UniversalCombobox
            url="/bills"
            labelKey="id"
            valueKey="id"
            selectedId={filters.billId}
            onSelectItem={(item) =>
              updateFilter('billId', item ? (item['id'] as number) : undefined)
            }
            placeholder={t('all')}
            className="w-full"
          />
        </FilterField>

        {/* Client */}
        <FilterField label={t('client')}>
          <UniversalCombobox
            url="/clients"
            labelKey="name"
            labelKeys={['lastname', 'name', 'middlename']}
            valueKey="id"
            selectedId={filters.clientId}
            onSelectItem={(item) =>
              updateFilter('clientId', item ? (item['id'] as number) : undefined)
            }
            placeholder={t('all')}
            className="w-full"
          />
        </FilterField>

        {/* Allocation (unallocated / advance payments) */}
        <FilterField label={t('allocation')}>
          <Select
            value={filters.unallocated ? 'unallocated' : 'all'}
            onValueChange={(v) =>
              updateFilter('unallocated', v === 'unallocated' ? true : undefined)
            }
          >
            <SelectTrigger className="w-full h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              <SelectItem value="unallocated">{t('unallocatedOnly')}</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>

        {/* Search */}
        <FilterField label={t('search')}>
          <div className="relative">
            <Input
              placeholder={t('search')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pr-10 h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
            />
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </FilterField>
      </CollapsibleFilters>

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="hover:bg-[#f8fafc]">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('noDataAvailable')}</h3>
                    <p className="text-[#64748b]">{t('trackDataToBetterManage')}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
